/**
 * Vuex getters for the "messages" module.
 *
 * Mostly thin state accessors, plus a few that filter/derive: split
 * direct-vs-group conversation lists, synthesize a display-only active
 * conversation for a not-yet-started chat, and default per-conversation
 * lookups (draft, pending contact, pagination flags) to safe empty values.
 */
export default {
  getConversations: (state) => state.conversations,
  // Legacy DM conversations were mapped before `type` existed, so a missing
  // type still counts as a direct message here rather than as a group.
  getDirectConversations: (state) => state.conversations.filter((c) => c.type !== 'group'),
  getGroupConversations: (state) => state.conversations.filter((c) => c.type === 'group'),
  getActiveConversationId: (state) => state.activeConversationId,
  getActiveConversation: (state) => {
    const existing = state.conversations.find((c) => c.id === state.activeConversationId);
    if (existing) return existing;
    // Not in the list yet (no message sent) — synthesize a display-only
    // conversation from the pending contact so the thread header still
    // shows their name/avatar.
    const pending = state.pendingContacts[state.activeConversationId];
    if (!pending) return null;
    return {
      id: state.activeConversationId,
      participant: pending.participant,
      lastMessagePreview: '',
      lastMessageThumbnailUrl: '',
      lastMessageAt: null,
      createdAt: null,
      unreadCount: 0,
    };
  },
  getActiveMessages: (state) =>
    state.activeConversationId
      ? state.messagesByConversation[state.activeConversationId] || []
      : [],
  getIsActiveConversationTyping: (state) =>
    state.activeConversationId
      ? Boolean(state.typingByConversation[state.activeConversationId])
      : false,
  getDraftForConversation: (state) => (conversationId) => state.draftsByConversation[conversationId] || '',
  getPendingContact: (state) => (conversationId) => state.pendingContacts[conversationId] || null,
  getMessagesStatus: (state) => state.status,
  getMessagesError: (state) => state.error,
  getUnreadMessagesCount: (state) => state.unreadMessagesCount,
  getConversationsSearch: (state) => state.conversationsSearch,
  getHasMoreConversations: (state) =>
    state.hasMoreConversationsByType.user || state.hasMoreConversationsByType.group,
  getIsLoadingMoreConversations: (state) => state.isLoadingMoreConversations,
  getHasMoreMessages: (state) => (conversationId) => state.hasMoreMessagesByConversation[conversationId] ?? true,
  getIsLoadingMoreMessages: (state) => (conversationId) =>
    Boolean(state.isLoadingMoreMessagesByConversation[conversationId]),
  getIsLoadingActiveMessages: (state) => state.isLoadingActiveMessages,
  getUnreadSnapshot: (state) => (conversationId) => state.unreadSnapshotByConversation[conversationId] || 0,
  getPeopleSearchResults: (state) => state.peopleSearchResults,
  getHasMorePeopleSearch: (state) => state.hasMorePeopleSearch,
  getIsLoadingMorePeopleSearch: (state) => state.isLoadingMorePeopleSearch,
  getIsSearchingPeople: (state) => state.isSearchingPeople,
  getAllUsersResults: (state) => state.allUsersResults,
  getIsSearchingAllUsers: (state) => state.isSearchingAllUsers,
  getDmAllowFrom: (state) => state.dmAllowFrom,
  getIsDmSettingsSaving: (state) => state.isDmSettingsSaving,
};
